import { useEffect, useMemo, useState } from 'react'
import { getFinanceiro } from '@/lib/api-shell'
import { VIEW_PATHS, type AppView } from '@/lib/navigation'
import { useTranslation } from 'react-i18next'

const ACCOUNT_NOTIFICATION_FINANCE_PAGE_SIZE = 100
const ACCOUNT_NOTIFICATION_FINANCE_MAX_PAGES = 20
const DAY_IN_MS = 24 * 60 * 60 * 1000

type FinanceNotificationKind = 'overdue' | 'due_soon'

export type AccountNotificationTone = 'neutral' | 'success' | 'warning' | 'danger'

export type AccountDistributionNotification = {
  tone: AccountNotificationTone
  statusLabel: string
  title: string
  description: string
}

export type AccountNotification = {
  key: string
  title: string
  description: string
  statusLabel: string
  tone: AccountNotificationTone
  href: string
  view: AppView
}

const normalizeFinanceStatus = (entry: Record<string, any>) =>
  String(entry.status || entry.situacao || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')

const resolveFinanceDueDate = (entry: Record<string, any>) => {
  const rawValue = entry.vencimento || entry.data_vencimento || entry.due_date
  if (typeof rawValue !== 'string' || !rawValue.trim()) return null

  const trimmedValue = rawValue.trim()
  const dateOnlyMatch = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  const parsedDate = dateOnlyMatch
    ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
    : new Date(trimmedValue)

  if (Number.isNaN(parsedDate.getTime())) return null
  return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate())
}

const resolveFinanceDaysUntilDue = (entry: Record<string, any>) => {
  const dueDate = resolveFinanceDueDate(entry)
  if (!dueDate) return null

  const today = new Date()
  const todayAtStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.round((dueDate.getTime() - todayAtStart.getTime()) / DAY_IN_MS)
}

const isFinanceOverdue = (entry: Record<string, any>) => {
  const normalizedStatus = normalizeFinanceStatus(entry)
  if (/(cancel|baixado)/.test(normalizedStatus)) return false
  if (normalizedStatus.includes('vencid')) return true
  if (/(nao pago|em aberto|aberto|pendente|vencid)/.test(normalizedStatus)) {
    const daysUntilDue = resolveFinanceDaysUntilDue(entry)
    return daysUntilDue !== null && daysUntilDue < 0
  }

  if (/(pago|liquid)/.test(normalizedStatus)) return false

  const daysUntilDue = resolveFinanceDaysUntilDue(entry)
  return daysUntilDue !== null && daysUntilDue < 0
}

const isFinanceDueSoon = (entry: Record<string, any>) => {
  const normalizedStatus = normalizeFinanceStatus(entry)
  if (/(cancel|baixado|pago|liquid|vencid)/.test(normalizedStatus)) return false

  const daysUntilDue = resolveFinanceDaysUntilDue(entry)
  return daysUntilDue !== null && daysUntilDue >= 0 && daysUntilDue <= 5
}

export function useAccountNotifications({
  isLoggedIn,
  distributionNotification = null,
  refreshKey,
}: {
  isLoggedIn: boolean
  distributionNotification?: AccountDistributionNotification | null
  refreshKey?: string
}) {
  const { t } = useTranslation()
  const [financeNotificationKind, setFinanceNotificationKind] = useState<FinanceNotificationKind | null>(null)

  useEffect(() => {
    let isMounted = true
    let idleCallbackId: number | null = null
    let timeoutId: number | null = null

    const loadFinanceNotification = async () => {
      if (!isLoggedIn) {
        if (isMounted) setFinanceNotificationKind(null)
        return
      }

      try {
        let nextNotificationKind: FinanceNotificationKind | null = null

        for (let page = 1; page <= ACCOUNT_NOTIFICATION_FINANCE_MAX_PAGES; page += 1) {
          const data = await getFinanceiro(page, ACCOUNT_NOTIFICATION_FINANCE_PAGE_SIZE, 'nao pago')
          const items = Array.isArray(data.items) ? data.items : []

          for (const entry of items) {
            if (!entry || typeof entry !== 'object') continue

            if (isFinanceOverdue(entry as Record<string, any>)) {
              nextNotificationKind = 'overdue'
              break
            }

            if (!nextNotificationKind && isFinanceDueSoon(entry as Record<string, any>)) {
              nextNotificationKind = 'due_soon'
            }
          }

          if (
            nextNotificationKind === 'overdue'
            || page >= data.totalPages
            || items.length < ACCOUNT_NOTIFICATION_FINANCE_PAGE_SIZE
          ) {
            break
          }
        }

        if (isMounted) {
          setFinanceNotificationKind(nextNotificationKind)
        }
      } catch {
        if (isMounted) {
          setFinanceNotificationKind(null)
        }
      }
    }

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleCallbackId = window.requestIdleCallback(() => {
        void loadFinanceNotification()
      }, { timeout: 1800 })
    } else {
      timeoutId = window.setTimeout(() => {
        void loadFinanceNotification()
      }, 1200)
    }

    return () => {
      isMounted = false
      if (idleCallbackId !== null && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackId)
      }
      if (timeoutId !== null && typeof window !== 'undefined') {
        window.clearTimeout(timeoutId)
      }
    }
  }, [isLoggedIn, refreshKey])

  return useMemo<AccountNotification[]>(() => ([
    ...(financeNotificationKind
      ? [{
          key: `finance-${financeNotificationKind}`,
          title: t(
            financeNotificationKind === 'overdue'
              ? 'header.account.notifications.finance_overdue_title'
              : 'header.account.notifications.finance_due_soon_title'
          ),
          description: t(
            financeNotificationKind === 'overdue'
              ? 'header.account.notifications.finance_overdue_description'
              : 'header.account.notifications.finance_due_soon_description'
          ),
          statusLabel: t(
            financeNotificationKind === 'overdue'
              ? 'account.finance.groups.overdue'
              : 'account.finance.groups.due_next_5'
          ),
          tone: financeNotificationKind === 'overdue' ? 'danger' as const : 'warning' as const,
          href: VIEW_PATHS.finance,
          view: 'finance' as const,
        }]
      : []),
    ...(distributionNotification
      ? [{
          key: 'distribution',
          title: distributionNotification.title,
          description: distributionNotification.description,
          statusLabel: distributionNotification.statusLabel,
          tone: distributionNotification.tone,
          href: VIEW_PATHS.benefits,
          view: 'benefits' as const,
        }]
      : []),
  ]), [distributionNotification, financeNotificationKind, t])
}
